declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

// The app's WebView appends "ProposalBizApp/<version> (<platform>; <build>)" to its user agent
// (getAppUserAgent in apps/mobile/src/config). Builds before the build number was added send
// "ProposalBizApp/<version> (<platform>)".
const NATIVE_APP_USER_AGENT_TOKEN = /ProposalBizApp\/(\S+) \((\w+)(?:; (\d+))?\)/;

export type NativeAppInfo = { platform: string; version: string; build: number | null };

// Server code passes the request's user-agent header; the browser's own is used otherwise.
export const getNativeAppInfo = (userAgent?: string | null): NativeAppInfo | null => {
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);
  const match = ua.match(NATIVE_APP_USER_AGENT_TOKEN);
  if (!match) return null;
  const [, version = '', platform = '', build] = match;
  return { platform, version, build: build ? Number(build) : null };
};

// Whether the page runs inside the app. Use it to decide what to show or where to send the user.
export const isNativeApp = (userAgent?: string | null): boolean =>
  getNativeAppInfo(userAgent) !== null;

// Whether this frame can post messages to the app. Check it before postMessage: the user agent
// is on every request the app makes, but the bridge may be missing in some frames (iframes).
export const hasNativeBridge = (): boolean =>
  typeof window !== 'undefined' && window.ReactNativeWebView !== undefined;

/**
 * Opens a URL in the device browser when inside the React Native app,
 * or in a new tab in a regular browser.
 */
export const openExternalLink = (url: string): void => {
  if (!url || typeof window === 'undefined') return;

  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'OPEN_IN_EXTERNAL_BROWSER', url }));
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
};
